<script setup lang="ts">
import type { Component } from "vue";
import { computed } from "vue";

import { AGENT_CONFIG_MAP } from "~/constants/agents/configs";
import EXECUTION_MODE_DISPLAY from "~/constants/agents/executionMode";
import { getLocalAgentDisplay, LOCAL_AGENT_KEY } from "~/constants/localAgent";
import { USER_KIND_DART_AI } from "~/constants/userKinds";
import { ChatIcon, ColorPickerIcon, CommandCenterIcon, DartboardIcon, SocialsIcon, UsersFieldIcon } from "~/icons";
import { AgentCategory, ExecutionMode } from "~/shared/enums";
import type { AgentConfig, User } from "~/shared/types";

const AGENT_CATEGORY_ICONS: Record<AgentCategory, Component> = {
  [AgentCategory.ASSISTANT]: ChatIcon,
  [AgentCategory.CODING]: CommandCenterIcon,
  [AgentCategory.SALES]: UsersFieldIcon,
  [AgentCategory.DESIGN]: ColorPickerIcon,
  [AgentCategory.MARKETING]: SocialsIcon,
};

const props = defineProps<{
  agent?: User;
  config?: AgentConfig;
}>();

const categoryDisplay = (category: AgentCategory) => ({ label: category, icon: AGENT_CATEGORY_ICONS[category] });

const display = computed<{ label: string; icon: Component }>(() => {
  if (props.config) {
    return categoryDisplay(props.config.category);
  }
  const { agent } = props;
  if (!agent) {
    throw new Error("AgentCategoryChip requires either an agent or a config");
  }
  if (agent.kind === USER_KIND_DART_AI) {
    return { label: "Project management", icon: DartboardIcon };
  }
  const config = AGENT_CONFIG_MAP.get(agent.kind);
  if (config) {
    return categoryDisplay(config.category);
  }
  if (agent.agent?.executionMode === ExecutionMode.LOCAL) {
    const localAgentDisplay = getLocalAgentDisplay(agent.agent.config?.[LOCAL_AGENT_KEY]);
    if (localAgentDisplay) {
      return { label: localAgentDisplay.chipLabel, icon: localAgentDisplay.icon };
    }
  }
  return EXECUTION_MODE_DISPLAY[agent.agent?.executionMode ?? ExecutionMode.PROMPT];
});
</script>

<template>
  <div
    class="flex w-fit max-w-full items-center gap-1.5 rounded-sm border border-md py-0.5 pr-1.5 pl-1 text-xs text-lt">
    <component :is="display.icon" class="size-3.5 shrink-0" />
    <span :title="display.label" class="min-w-0 truncate">{{ display.label }}</span>
  </div>
</template>
